import { denormalizeLocale, localeCurrency, type Locale } from '@/i18n/config';

/**
 * Format a price value according to the locale's currency.
 */
export function formatCurrency(price: number, locale: string): string {
  const normalizedLocale = denormalizeLocale(locale) as Locale;
  const currencyConfig = localeCurrency[normalizedLocale];

  return new Intl.NumberFormat(normalizedLocale, {
    style: 'currency',
    currency: currencyConfig.code,
  }).format(price);
}

/**
 * Normalize phone number for display with country prefix.
 */
export function formatPhoneForDisplay(phone: string, locale: string): string {
  if (!phone) return '';

  const normalizedLocale = denormalizeLocale(locale) as Locale;
  let cleanPhone = phone.replace(/[^\d+()]/g, '').replace(/\s/g, '');

  if (cleanPhone.includes('(+')) {
    cleanPhone = cleanPhone.replace(/\(/g, '').replace(/\)/g, '');
  }

  if (cleanPhone.startsWith('+')) {
    if (cleanPhone.startsWith('+55') && cleanPhone.length > 3) {
      const number = cleanPhone.substring(3);
      return `(+55) ${number}`;
    } else if (cleanPhone.startsWith('+1') && cleanPhone.length > 2) {
      const number = cleanPhone.substring(2);
      return `(+1) ${number}`;
    }
    return cleanPhone;
  }

  if (normalizedLocale === 'pt-BR') {
    if (cleanPhone.startsWith('55') && cleanPhone.length >= 12) {
      const number = cleanPhone.substring(2);
      return `(+55) ${number}`;
    }
    return `(+55) ${cleanPhone}`;
  } else {
    if (cleanPhone.startsWith('1') && cleanPhone.length === 11) {
      const secondDigit = cleanPhone.substring(1, 2);
      const isLikelyBrazilian = secondDigit === '1' || secondDigit === '9';
      if (!isLikelyBrazilian) {
        const number = cleanPhone.substring(1);
        return `(+1) ${number}`;
      }
    }
    return `(+1) ${cleanPhone}`;
  }
}

export function normalizePhoneNumber(phone: string, locale: string): string {
  if (phone.startsWith('__uid__')) return phone;

  let cleanPhone = phone.replace(/[^\d+]/g, '').replace(/\s/g, '');

  cleanPhone = cleanPhone.replace(/^\(\+(\d+)\)\s*/, '+$1');

  if (cleanPhone.startsWith('+')) {
    return cleanPhone;
  }

  const normalizedLocale = denormalizeLocale(locale) as Locale;

  if (normalizedLocale === 'pt-BR') {
    if (cleanPhone.startsWith('55') && cleanPhone.length >= 12) {
      return '+' + cleanPhone;
    }
    return '+55' + cleanPhone;
  } else {
    const secondDigit = cleanPhone.length > 1 ? cleanPhone.substring(1, 2) : '';
    const isLikelyBrazilian = secondDigit === '1' || secondDigit === '9';

    if (cleanPhone.startsWith('1') && cleanPhone.length === 11 && !isLikelyBrazilian) {
      return '+' + cleanPhone;
    }
    return '+1' + cleanPhone;
  }
}

/**
 * Sanitize TIN (Tax Identification Number) to only letters and numbers.
 */
export function sanitizeTin(tin: string | null | undefined): string {
  if (tin == null || typeof tin !== 'string') return '';
  return tin.replace(/[^a-zA-Z0-9]/g, '');
}

/** Deterministic 8-digit code from an arbitrary string. */
function hashStringToEightDigits(input: string): string {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }

  const positiveHash = Math.abs(hash);
  const hashStr = positiveHash.toString();

  if (hashStr.length >= 8) {
    return hashStr.slice(-8);
  }

  const padded = hashStr.padStart(8, '0');

  if (padded === '00000000') {
    let fallback = '';
    for (let i = 0; i < Math.min(input.length, 8); i++) {
      const charCode = input.charCodeAt(i);
      fallback += (charCode % 10).toString();
    }
    return fallback.padStart(8, '0');
  }

  return padded;
}

/**
 * Generate a deterministic 8-digit OTP from a store ID (Vupt Pager).
 */
export function generateStoreOtp(storeId: string): string {
  return hashStringToEightDigits(storeId);
}

/** 8-digit printer connection code; distinct from store OTP for same store. */
export function generatePrinterOtp(storeId: string): string {
  return hashStringToEightDigits(`${storeId}|vupt-printer`);
}

/** 8-digit importer connection code; distinct from store and printer OTPs. */
export function generateImporterOtp(storeId: string): string {
  return hashStringToEightDigits(`${storeId}|vupt-importer`);
}
